import { createContext, useContext } from 'react';
import type { AlertButton, AlertOptions } from 'react-native';

import type {
	NotificationAction,
	NotificationItem,
	NotificationOptions,
	NotificationVariant,
} from '@/utils/notification-manager';

/**
 * The notification context on its own, apart from the provider. The provider renders the toast
 * and modal UI and the retry bridge (which reaches the API service), so a hook that only needs
 * to CALL `showToast` imports this module and none of that.
 */
export interface NotificationContextType {
	// showToast returns the notification id so callers can dismiss it
	// programmatically (e.g. when a retry succeeds or finally fails).
	showToast: (message: string, options?: NotificationOptions) => string;
	showModal: (props: {
		title: string,
		variant?: NotificationVariant,
		message?: string,
		actions?: NotificationAction[],
	}) => void;
	showErrorModal: (error: any, title?: string) => void;
	showBanner: (message: string, variant?: NotificationVariant) => void;
	alert: (
		title: string,
		message?: string,
		buttons?: AlertButton[],
		options?: AlertOptions,
	) => void;
	dismiss: (id: string) => void;
	dismissAll: () => void;
	getNotifications: () => NotificationItem[];
	notifications: NotificationItem[];
}

export const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = (): NotificationContextType => {
	const context = useContext(NotificationContext);
	if (!context) {
		throw new Error('useNotification must be used within a NotificationProvider');
	}
	return context;
};

/** The context when a provider is mounted, else undefined — for callers that can do without toasts. */
export const useOptionalNotification = (): NotificationContextType | undefined => useContext(NotificationContext);
